import { FormGroup, Input, Label, Tooltip } from "reactstrap";
import { useThemeContext } from "../../Context/ThemeContext";
import { getDarkDropdown } from "../../Utils/otherFunctions";
import { forwardRef, useState, useEffect } from "react";
import { Info } from "react-feather";

const CheckboxSelectField = forwardRef(function CheckboxSelectField(props, ref) {
  const { 
    label, 
    options, 
    fieldState, 
    className,
    value = [],
    onChange,
    withTextInputs = {}, 
    textInputValues = {},
    textInputPlaceholders = {},
    onTextInputChange = () => {},
    disabled = false, // Add explicit disabled prop with default value
  } = props;
  
  const { themeContext } = useThemeContext();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const isOptionsEmpty = options?.length <= 0;

  const handleCheckboxChange = (optionValue, checked) => {
    // If component is disabled, don't allow changes
    if (disabled) {
      return;
    }
    
    let updatedValues = [...(value || [])];
    
    if (checked) {
      if (!updatedValues.includes(optionValue)) {
        updatedValues.push(optionValue);
      }
    } else {
      updatedValues = updatedValues.filter(item => item !== optionValue);
    }
    
    if (onChange) {
      onChange({
        target: {
          name: props.name,
          value: updatedValues
        }
      });
    }
  };

  const isChecked = (optionValue) => {
    return Array.isArray(value) && value.includes(optionValue);
  };

  return (
    <FormGroup className={className}>
      {label && (
        <Label className={`bg-transparent ${disabled ? 'text-muted' : ''}`}>
          {label}{" "}
          {props.isRequired && (
            <font className="ml-1" color={disabled ? "grey" : "red"}>
              *
            </font>
          )}
          {props.tooltip && <Info id={`tooltip-${props.name}`} size={10} />}
        </Label>
      )}

      <div className={`checkbox-options-container ${disabled ? 'opacity-75' : ''}`}>
        {options.map((option) => (
          <div key={option.id || option.value}>
            <div className={"d-flex align-items-center mb-2"}>
              <Input
                className={`m-0 ${getDarkDropdown(themeContext)} ${isChecked(option.value || option.id) ? 'btn-dark' : ''}`}
                disabled={isOptionsEmpty || disabled || option.disabled}
                invalid={fieldState?.error && !disabled}
                type="checkbox"
                id={`${props.name}-${option.id || option.value}`}
                name={props.name}
                value={option.value || option.id}
                checked={isChecked(option.value || option.id)}
                onChange={(e) => handleCheckboxChange(option.value || option.id, e.target.checked)}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <Label 
                for={`${props.name}-${option.id || option.value}`} 
                className={`m-0 px-2 ${disabled ? 'text-muted' : ''}`}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
              >
                {option.name}
              </Label>
            </div>
            
            {withTextInputs[option.value || option.id] && 
              isChecked(option.value || option.id) && (
              <div className="mb-3 ml-4">
                <Input
                  type="text"
                  name={`${props.name}-${option.value || option.id}-text`}
                  value={textInputValues[option.value || option.id] || ""}
                  onChange={(e) => {
                    if (!disabled) {
                      onTextInputChange(option.value || option.id, e.target.value);
                    }
                  }}
                  placeholder={textInputPlaceholders[option.value || option.id] || "Masukkan detail"}
                  className="form-control"
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {fieldState?.error && !disabled && (
        <Label className={"label-text text-danger"}>
          {fieldState.error.message}
        </Label>
      )}

      {props.tooltip && (
        <Tooltip
          placement="top"
          isOpen={tooltipOpen}
          target={`tooltip-${props.name}`}
          toggle={toggle}
        >
          {props.tooltip}
        </Tooltip>
      )}

      <style jsx>{`
        .checkbox-options-container {
          display: flex;
          flex-direction: column;
        }
        
        .opacity-75 {
          opacity: 0.75;
        }
      `}</style>
    </FormGroup>
  );
});

export default CheckboxSelectField;